import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { ApprovalStatus } from '@/types/database'
import type { ApprovalWithPeople } from '@/types/domain'

const SELECT = `
  *,
  task:tasks!inner(id,title,frente,workspace_id),
  requester:profiles!approvals_requested_by_fkey(id,full_name,avatar_url,email),
  approver:profiles!approvals_approver_id_fkey(id,full_name,avatar_url,email)
`

export function useApprovals() {
  const { workspaceId } = useWorkspace()
  return useQuery({
    queryKey: qk.approvals(workspaceId ?? 'none'),
    enabled: !!workspaceId,
    queryFn: async (): Promise<ApprovalWithPeople[]> => {
      const { data, error } = await supabase
        .from('approvals')
        .select(SELECT)
        .eq('task.workspace_id', workspaceId!)
        .order('created_at', { ascending: false })
        .returns<ApprovalWithPeople[]>()
      if (error) throw error
      return data ?? []
    },
  })
}

function useInvalidateApprovals() {
  const qc = useQueryClient()
  const { workspaceId } = useWorkspace()
  return () => {
    void qc.invalidateQueries({ queryKey: qk.approvals(workspaceId ?? 'none') })
    void qc.invalidateQueries({ queryKey: qk.tasks(workspaceId ?? 'none') })
    void qc.invalidateQueries({ queryKey: qk.dashboard(workspaceId ?? 'none') })
  }
}

export function useRequestApproval() {
  const { user } = useAuth()
  const { workspaceId } = useWorkspace()
  const invalidate = useInvalidateApprovals()
  return useMutation({
    mutationFn: async ({
      taskId,
      approverId,
      comment,
    }: {
      taskId: string
      approverId?: string | null
      comment?: string
    }) => {
      const { count } = await supabase
        .from('approvals')
        .select('id', { count: 'exact', head: true })
        .eq('task_id', taskId)
      const round = (count ?? 0) + 1

      const { error } = await supabase.from('approvals').insert({
        task_id: taskId,
        requested_by: user?.id ?? null,
        approver_id: approverId ?? null,
        status: 'pendente',
        round,
        comment: comment ?? null,
      })
      if (error) throw error

      await supabase.from('tasks').update({ status: 'aguardando_aprovacao' }).eq('id', taskId)

      if (approverId && workspaceId) {
        await supabase.from('notifications').insert({
          workspace_id: workspaceId,
          user_id: approverId,
          type: 'aprovacao_solicitada',
          title: 'Aprovação solicitada',
          body: comment ?? null,
          entity_type: 'task',
          entity_id: taskId,
        })
      }
    },
    onSuccess: () => invalidate(),
  })
}

export function useDecideApproval() {
  const invalidate = useInvalidateApprovals()
  return useMutation({
    mutationFn: async ({
      id,
      taskId,
      status,
      comment,
    }: {
      id: string
      taskId: string
      status: Exclude<ApprovalStatus, 'pendente'>
      comment?: string
    }) => {
      const { error } = await supabase
        .from('approvals')
        .update({ status, comment: comment ?? null })
        .eq('id', id)
      if (error) throw error

      // Reflete a decisão no fluxo da tarefa.
      const nextStatus = status === 'aprovado' ? 'agendado' : 'em_ajustes'
      await supabase.from('tasks').update({ status: nextStatus }).eq('id', taskId)
    },
    onSuccess: () => invalidate(),
  })
}
